import { ThemeKey } from "@smartsheet/design-tokens";
import React, { useMemo } from "react";
import { SupportedLocales } from "../../../../i18n";
import { useTheme } from "../../../../theme/utils/useTheme";
import { DatePickerDate } from "../../types";
import { DatePickerYearSelectorStyled } from "../styled/DatePickerYearSelectorStyled";

export interface DatePickerYearSelectorProps {
    locale: SupportedLocales;
    maxDate: DatePickerDate | undefined;
    minDate: DatePickerDate | undefined;
    onChange: (year: number) => void;
    selectedYear: number;
}

export const DatePickerYearSelector = ({
    locale,
    maxDate,
    minDate,
    onChange,
    selectedYear,
}: DatePickerYearSelectorProps) => {
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const userProvidedMinYear = minDate ? new Date(minDate.year, minDate.month, minDate.day).getFullYear() : null;
    const userProvidedMaxYear = maxDate ? new Date(maxDate.year, maxDate.month, maxDate.day).getFullYear() : null;

    const allYears = useMemo<{ year: number }[]>(
        () => getYearOptions(selectedYear, userProvidedMinYear, userProvidedMaxYear),
        [selectedYear, userProvidedMinYear, userProvidedMaxYear]
    );
    return (
        <DatePickerYearSelectorStyled
            options={allYears}
            onChange={(value) => {
                if (value === undefined || value === null || value.year === selectedYear) {
                    // This won't really happen since this select isn't clearable.
                    return;
                }
                onChange(value.year);
            }}
            selectedOption={allYears.find((option) => option.year === selectedYear) || null}
            getOptionKey={(option) => option.year}
            optionToString={(option) => option?.year.toString() || ""}
            locale={locale}
            isClearable={false}
            themeMode={themeMode}
        />
    );
};

const DEFAULT_MIN_YEARS = 15;
const DEFAULT_MAX_YEARS = 15;

const getYearOptions = (selectedYear: number, providedStart: number | null, providedEnd: number | null) => {
    const start = providedStart ?? selectedYear - DEFAULT_MIN_YEARS;
    const end = providedEnd ?? selectedYear + DEFAULT_MAX_YEARS;

    return Array.from({ length: end - start + 1 }).map((_, index) => {
        const year = start + index;
        return { year };
    });
};
